import { FocusMonitor } from '@angular/cdk/a11y';
import { AsyncPipe, NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import {
  CLIENT_APP_ID,
  Car,
  ClientAppId,
  Dossier,
  alphaNumPattern,
  emailPattern,
  generateNewCar,
  getCurrentCarDescription,
  getMatchingPhonePattern,
  isSameLicensePlate,
  phonePatterns,
} from '@sc/data-access-models';
import {
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import {
  FeatureFlippingManagerService,
  FeatureToggleDirective,
} from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { ProductTourService } from '@sc/feature-product-tour';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  AsteriskInfoComponent,
  ButtonComponent,
  CustomValidators,
  DatePickerComponent,
  ErrorMessagesService,
  GpdrComponent,
  InputTextComponent,
  LicensePlateNumberValidator,
  MarketingDataCheckboxComponent,
  PhoneInputComponent,
  SpinnerComponent,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { TourService } from 'ngx-ui-tour-md-menu';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  combineLatest,
} from 'rxjs';
import { delayWhen, switchMap, take, tap } from 'rxjs/operators';
import { InsurerDeclarationSteps } from '../../../../models';

@Component({
  selector: 'sc-customer-data-form',
  styleUrls: ['./customer-data-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputTextComponent,
    NgIf,
    SpinnerComponent,
    NgStyle,
    FeatureToggleDirective,
    MatRadioModule,
    DatePickerComponent,
    GpdrComponent,
    MarketingDataCheckboxComponent,
    ButtonComponent,
    AsyncPipe,
    TranslateModule,
    PhoneInputComponent,
    AsteriskInfoComponent,
  ],
  template: `
    <h1 class="sc-design-title-h1">
      {{ 'CustomerDataForm.title' | translate }}
    </h1>
    <form class="customer-data-form" [formGroup]="form" sc-focus-return-zone>
      <sc-asterisk-info></sc-asterisk-info>

      <fieldset>
        <legend class="sc-form-title-desktop sc-design-title-h2">
          {{ 'CustomerDataForm.carModelTitle' | translate }}
        </legend>

        <div class="form-row">
          <div class="form-data">
            <sc-input-text
              formControlName="licensePlateNumber"
              scKeyPress="licensePlate"
              (input)="onValidLicensePlate($any($event).target.value)"
              [label]="'CustomerDataForm.licensePlateInpt' | translate"
              [hint]="getLicensePlateTooltipValue()"
            ></sc-input-text>
          </div>

          <div class="form-data license-plate-infos-form-data">
            <div
              id="license-plate-infos"
              tabindex="0"
              class="car-name"
              [class.error]="!currentCar?.model && !currentCar?.manufacturer"
              *ngIf="
                (displayLoading | async) === false &&
                hasSearchCar &&
                canSearchLicensePlate()
              "
            >
              <p>{{ getCarDisplayInfos() }}</p>
            </div>
            <div
              class="car-loading"
              *ngIf="
                (displayLoading | async) === true && canSearchLicensePlate()
              "
            >
              <sc-spinner [size]="30"></sc-spinner>
            </div>
          </div>
        </div>
        <div class="form-row">
          <div
            class="form-data"
            [ngStyle]="{
              display: displayContractNumberField()
            }"
          >
            <sc-input-text
              id="contractNumberInput"
              #contractNumber
              formControlName="contractNumber"
              [label]="'CustomerDataForm.contractNumberInpt' | translate"
            ></sc-input-text>
          </div>

          <div class="form-data">
            <sc-input-text
              id="insurerCompanyInput"
              *ngIf="
                !featureFlippingManager.isFeatureActivated(
                  'b2b-field-radio-selection-insurer-company'
                )
              "
              #insurerCompany
              formControlName="insurerCompany"
              [label]="'CustomerDataForm.insurerCompanyInpt' | translate"
              errorPattern="alphaNumPattern"
            ></sc-input-text>
            <label
              *scFeatureToggle="['b2b-field-radio-selection-insurer-company']"
              >{{ 'CustomerDataForm.insurerCompanyInpt' | translate }}</label
            >
            <span
              *scFeatureToggle="['b2b-field-radio-selection-insurer-company']"
              class="insurerCompany"
            >
              <mat-radio-group
                formControlName="insurerCompanyCode"
                (change)="onInsurerCompanyChange($event)"
              >
                <mat-radio-button
                  #daChoice
                  [value]="configurationService?.getConfig()?.id"
                  [checked]="
                    configurationService?.getConfig()?.id ===
                    form?.get('insurerCompanyCode')?.value
                  "
                >
                  {{ configurationService?.getConfig()?.insurerName }}
                </mat-radio-button>
                <mat-radio-button
                  #otherChoice
                  value="other"
                  [checked]="'other' === form?.get('insurerCompanyCode')?.value"
                  >{{
                    'CustomerDataForm.otherRadioChoice' | translate
                  }}</mat-radio-button
                >
              </mat-radio-group>
              <sc-input-text
                (input)="onOtherInsurerCompanyChange($event)"
                [disabled]="!otherChoice.checked"
                #insurerCompanyDA
                errorPattern="alphaNumPattern"
              ></sc-input-text>
            </span>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend class="sc-form-title-desktop sc-design-title-h2">
          {{ 'CustomerDataForm.customerFormTitle' | translate }}
        </legend>
        <div class="form-row">
          <div class="form-data">
            <sc-input-text
              id="customerFirstNameInput"
              formControlName="firstname"
              [label]="'CustomerDataForm.firstnameInpt' | translate"
              errorPattern="alphaNumPattern"
            ></sc-input-text>
          </div>
          <div class="form-data">
            <sc-input-text
              id="customerLastNameInput"
              formControlName="lastname"
              [label]="'CustomerDataForm.nameInpt' | translate"
              errorPattern="alphaNumPattern"
            ></sc-input-text>
          </div>
        </div>
        <div class="form-row">
          <div class="form-data">
            <sc-input-text
              id="customerEmailInput"
              formControlName="email"
              [label]="'CustomerDataForm.addressInpt' | translate"
              errorPattern="emailPattern"
              [hint]="displayHint() | translate"
            ></sc-input-text>
          </div>

          <div class="form-data">
            <sc-phone-input
              id="customerPhoneInput"
              formControlName="phoneNumber"
              [label]="'CustomerDataForm.phoneNumberInpt' | translate"
              scKeyPress="phoneNumber"
              [displayCountrySelection]="true"
              [setCountry]="form.get('phoneCountry').value"
              (changeCountry)="form.get('phoneCountry').setValue($event)"
              [required]="
                !featureFlippingManager.isFeatureActivated(
                  'b2b-allow-only-one-contact-channel-phone-or-email'
                )
              "
            ></sc-phone-input>
          </div>
        </div>
      </fieldset>

      <fieldset *ngIf="shouldDisplaySinisterDate()">
        <legend class="sc-form-title-desktop sc-design-title-h2">
          {{ 'CustomerDataForm.sinisterDate' | translate }}
        </legend>

        <div class="form-row">
          <div class="form-data">
            <sc-date-picker
              id="sinisterDateInput"
              formControlName="sinisterDate"
              [label]="'CustomerDataForm.sinisterDate' | translate"
              [maxDate]="maxAcceptedSinisterDate"
              [isRequired]="isSinisterDateRequired()"
              [hint]="
                'GlobalApplicativeConstants.sinisterDateHintMessage' | translate
              "
            ></sc-date-picker>
          </div>
        </div>
      </fieldset>

      <div
        class="form-row"
        *ngIf="
          featureFlippingManager.isFeatureActivated(
            'b2b-field-required-gpdr'
          ) ||
          featureFlippingManager.isFeatureActivated(
            'b2b-field-display-marketing-data'
          )
        "
      >
        <div *scFeatureToggle="'b2b-field-required-gpdr'" class="form-data">
          <sc-gpdr formControlName="gpdr" [isCustomer]="false"></sc-gpdr>
        </div>
        <div
          *scFeatureToggle="'b2b-field-display-marketing-data'"
          class="form-data"
        >
          <sc-marketing-data-checkbox
            formControlName="marketingData"
            [isCustomer]="false"
          ></sc-marketing-data-checkbox>
        </div>
      </div>

      <sc-button
        class="customer-data-form-button"
        id="nextButton"
        [disabled]="!(form.valid && (displayLoading | async) === false)"
        [buttonType]="'primary'"
        (click)="onValidate()"
        >{{ 'CustomerDataForm.validateBtn' | translate }}</sc-button
      >
    </form>
  `,
})
export class CustomerDataFormComponent implements OnInit, OnDestroy {
  form: FormGroup;
  currentDossier: Dossier = null;
  currentCar: Car = null;
  displayLoading = new BehaviorSubject<boolean>(false);
  maxAcceptedSinisterDate = new Date();

  dossierSubscriber: Subscription = null;
  userLoginSubscriber: Subscription = null;
  licensePlateHasTheFocus = false;
  tipsHidden: boolean;
  hasSearchCar = false;
  insurer: string;

  isDossierAccess = false;

  private userLogin: string;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dossierSB: DossierSandboxService,
    private readonly sandbox: SandboxService,
    private readonly focusMonitor: FocusMonitor,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly translator: TranslatorService,
    public configurationService: ConfigurationService,
    public errorMessagesService: ErrorMessagesService,
    private readonly snackbarService: SnackbarService,
    public featureFlippingManager: FeatureFlippingManagerService,
    private readonly tourService: TourService,
    private readonly productTourService: ProductTourService,
    private readonly insightService: InsightService,
    private readonly storage: StorageService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    this.insightService.trackPageView({
      name: 'New Dossier form page',
    });

    this.initNavigation();
  }

  initNavigation() {
    this.sandbox.setCurrentStep(
      InsurerDeclarationSteps.identificationInsuredStep
    );
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: any) {
    if (e?.srcElement?.nodeName === 'INPUT' && e?.key === 'Enter') {
      const currentInputName = e?.srcElement?.classList;
      let nextInput;
      for (let index = 0; index < e?.target?.form?.length; index++) {
        if (
          currentInputName
            ?.toString()
            ?.includes(e?.target?.form[index]?.classList)
        ) {
          nextInput = e?.target?.form[index + 1];
          break;
        }
      }
      if (nextInput) {
        e.preventDefault();
        this?.focusMonitor?.focusVia(nextInput, 'program');
      }
    }
  }

  ngOnInit(): void {
    this.snackbarService.setFocusManagement(true, {
      documentQuery: 'sc-customer-data-form form',
    });
    window.scrollTo(0, 0);
    this.generateForm();
    this.currentCar = null;
    this.isDossierAccess = this.authService.isDossierAccess() === true;

    this.userLoginSubscriber = this.authService
      .getUserLogin$()
      .subscribe((userLogin) => (this.userLogin = userLogin));

    this.dossierSubscriber = this.dossierSB.dossier$
      .pipe(take(1))
      .subscribe((dossier: Dossier) => {
        this.currentDossier = dossier;
        this.currentCar = dossier ? dossier.claim.car : null;
        this.generateForm();
        this.reloadDossierData(dossier);
        this.displayLoading.next(false);
        this.changeDetectorRef.detectChanges();
        if (this.currentCar) {
          this.onValidLicensePlate(this.currentCar?.licensePlateNumber);
        }
      });

    combineLatest([
      this.translator.getTranslation(
        'CustomerDataForm.productTour.sendLink.title'
      ),
      this.translator.getTranslation(
        'CustomerDataForm.productTour.sendLink.content'
      ),
    ]).subscribe(([title, content]) => {
      this.tourService.initialize([
        {
          anchorId: 'validation-send-link',
          title,
          content,
        },
      ]);
    });

    if (
      this.featureFlippingManager.isFeatureActivated(
        'send-customer-access-email'
      )
    ) {
      this.productTourService.setDisplayHelp(true);
    }

    if (this.storage.getDisplayProductTourOn(this.router.url)) {
      this.tourService.start();
    } else {
      this.tipsHidden = true;
      this.changeDetectorRef.detectChanges();
    }

    this.tourService.start$.subscribe(() => {
      this.tipsHidden = false;
      this.changeDetectorRef.detectChanges();
    });

    this.tourService.end$.subscribe(() => {
      this.tipsHidden = true;
      this.changeDetectorRef.detectChanges();
      this.storage.setDisplayProductTourOn(this.router.url, false);
    });
  }

  reloadDossierData(dossier: Dossier): void {
    if (dossier && dossier.customer) {
      this.form.patchValue({
        firstname: dossier.customer?.firstname,
        lastname: dossier.customer?.lastname,
        email:
          dossier.customer.email !== 'noreply@saint-gobain.com'
            ? dossier.customer.email
            : '',
        phoneNumber: dossier.customer.phoneNumber?.number?.trim(),
        phoneCountry: dossier.customer.phoneNumber?.countryCode,
        insurerCompany: dossier.policyInformation?.insuranceName,
        insurerCompanyCode: dossier?.policyInformation?.insuranceCode,
        contractNumber: dossier.policyInformation?.policyNumber,
        licensePlateNumber: dossier.claim.car?.licensePlateNumber,
        sinisterDate: dossier.claim?.sinisterDate,
        sinisterComment: dossier.claim?.sinisterComment,
        gpdr: dossier?.gpdr,
      });

      this.refreshFormGroup();
    }
  }

  private refreshFormGroup(): void {
    this.form.markAllAsTouched();
    for (const inner in this.form.controls) {
      if (inner) {
        this.form.get(inner).markAsTouched();
        this.form.get(inner).updateValueAndValidity();
      }
    }
  }

  shouldDisplaySinisterDate(): boolean {
    return (
      this.featureFlippingManager.isFeatureActivated(
        'b2b-field-required-sinister-date'
      ) || this.isSinisterDateOptional()
    );
  }

  isSinisterDateOptional(): boolean {
    return this.featureFlippingManager.isFeatureActivated(
      'b2b-field-optional-sinister-date'
    );
  }

  isSinisterDateRequired(): boolean {
    return this.shouldDisplaySinisterDate() && !this.isSinisterDateOptional();
  }

  generateForm(fullReset = false): void {
    if (fullReset) {
      this.currentCar = null;
      this.hasSearchCar = false;
      this.currentDossier = null;
    }
    this.form = this.formBuilder.group(
      {
        firstname: [
          {
            value: '',
            disabled:
              this.featureFlippingManager.isFeatureActivated(
                'b2b-non-editable-form'
              ) &&
              !!this.currentDossier?.customer?.firstname &&
              this.isDossierAccess,
          },
          [
            this.featureFlippingManager.isFeatureActivated(
              'b2b-allow-null-firstname'
            )
              ? Validators.nullValidator
              : Validators.required,
            Validators.maxLength(100),
            Validators.pattern(alphaNumPattern),
          ],
        ],
        lastname: [
          {
            value: '',
            disabled:
              this.featureFlippingManager.isFeatureActivated(
                'b2b-non-editable-form'
              ) &&
              !!this.currentDossier?.customer?.lastname &&
              this.isDossierAccess,
          },
          [
            Validators.required,
            Validators.maxLength(100),
            Validators.pattern(alphaNumPattern),
          ],
        ],
        email: [
          '',
          [
            this.featureFlippingManager.isFeatureActivated(
              'b2b-allow-only-one-contact-channel-phone-or-email'
            )
              ? Validators.nullValidator
              : Validators.required,
            Validators.pattern(emailPattern),
            Validators.maxLength(100),
          ],
        ],
        phoneNumber: [''],
        phoneCountry: [
          this.configurationService.getDefaultPhoneNumberCountry(),
        ],
        insurerCompany: this.featureFlippingManager.isFeatureActivated(
          'b2b-field-radio-selection-insurer-company'
        )
          ? [
              {
                value: '',
                disabled:
                  this.featureFlippingManager.isFeatureActivated(
                    'b2b-non-editable-form'
                  ) &&
                  !!this.currentDossier?.policyInformation?.insuranceName &&
                  this.isDossierAccess,
              },
              [
                Validators.required,
                Validators.maxLength(100),
                Validators.pattern(alphaNumPattern),
              ],
            ]
          : [
              {
                value: this.configurationService?.getConfig()?.insurerName,
                disabled:
                  this.featureFlippingManager.isFeatureActivated(
                    'b2b-read-only-insurer-company-name'
                  ) ||
                  (this.featureFlippingManager.isFeatureActivated(
                    'b2b-non-editable-form'
                  ) &&
                    !!this.currentDossier?.policyInformation?.insuranceName &&
                    this.isDossierAccess),
              },
              [
                Validators.required,
                Validators.maxLength(100),
                Validators.pattern(alphaNumPattern),
              ],
            ],
        insurerCompanyCode: this.featureFlippingManager.isFeatureActivated(
          'b2b-field-radio-selection-insurer-company'
        )
          ? [
              '',
              [
                Validators.required,
                Validators.maxLength(100),
                Validators.pattern(alphaNumPattern),
              ],
            ]
          : this.configurationService?.getConfig()?.insuranceSuggestion[0]
          ? [
              this.configurationService?.getConfig()?.insuranceSuggestion[0]
                .insuranceCountryCode +
                this.configurationService?.getConfig()?.insuranceSuggestion[0]
                  .insuranceCode,
            ]
          : [this.configurationService?.getConfig()?.id],
        contractNumber: [
          {
            value: '',
            disabled:
              this.featureFlippingManager.isFeatureActivated(
                'b2b-non-editable-form'
              ) &&
              !!this.currentDossier?.policyInformation?.policyNumber &&
              this.isDossierAccess,
          },
          this.getContractNumberValidators(),
        ],
        licensePlateNumber: [
          {
            value: '',
            disabled:
              this.featureFlippingManager.isFeatureActivated(
                'b2b-non-editable-form'
              ) &&
              !!this.currentCar?.licensePlateNumber &&
              this.isDossierAccess,
          },
          [
            Validators.required,
            LicensePlateNumberValidator.pattern(
              this.configurationService.getAllLicensePlateCountries()
            ),
          ],
        ],
        gpdr: [null, this.getGpdrValidators()],
        marketingData: [null],
        sinisterDate: [
          {
            value: null,
            disabled:
              this.featureFlippingManager.isFeatureActivated(
                'b2b-non-editable-form'
              ) &&
              !!this.currentDossier?.claim?.sinisterDate &&
              this.isDossierAccess,
          },
          [
            this.isSinisterDateRequired()
              ? Validators.required
              : Validators.nullValidator,
          ],
        ],
      },
      this.featureFlippingManager.isFeatureActivated(
        'b2b-allow-only-one-contact-channel-phone-or-email'
      )
        ? {
            validator: CustomValidators.atLeastOneOfTwo('phoneNumber', 'email'),
          }
        : null
    );

    this.changeDetectorRef.markForCheck();

    this.snackbarService.setFocusManagementForm(this.form);
  }

  private getContractNumberValidators(): ValidatorFn[] {
    const validators = [Validators.maxLength(100)];

    if (
      this.featureFlippingManager.isFeatureActivated(
        'b2b-field-required-contract-number'
      )
    ) {
      validators.push(Validators.required);
    }
    return validators;
  }

  private getGpdrValidators(): ValidatorFn[] {
    const validators = [];
    if (
      this.featureFlippingManager.isFeatureActivated('b2b-field-required-gpdr')
    ) {
      validators.push(Validators.required);
    }
    return validators;
  }

  displayContractNumberField(): string {
    return this.featureFlippingManager.isFeatureActivated(
      'b2b-field-display-contract-number'
    ) ||
      this.featureFlippingManager.isFeatureActivated(
        'b2b-field-required-contract-number'
      )
      ? 'bloc'
      : 'none';
  }

  formGroupToDossier(): Partial<Dossier> {
    const insuranceChoice =
      this.currentDossier?.policyInformation?.insuranceChoice ||
      (this.form.get('insurerCompanyCode')?.value ||
      this.form.get('insurerCompany')?.value
        ? 'true'
        : 'unknown');

    const sinisterDate = this.form.get('sinisterDate')?.value;

    const formPhoneNumber = this.form.get('phoneNumber')?.value;
    const formCountryCode = this.form.get('phoneCountry')
      ?.value as keyof typeof phonePatterns;
    const usedInternationalCode = getMatchingPhonePattern(
      formPhoneNumber,
      formCountryCode
    )?.code;

    const insurance = this.getUniqueInsuranceSuggestion();

    return {
      ...this.currentDossier,
      customer: {
        ...this.currentDossier?.customer,
        firstname: this.form.get('firstname')?.value,
        lastname: this.form.get('lastname')?.value,
        email: this.form.get('email')?.value,
        phoneNumber: {
          number: this.dossierSB.formatCustomerPhoneNumber(formPhoneNumber),
          countryCode: formCountryCode,
          internationalCode: usedInternationalCode,
        },
      },
      claim: {
        ...this.currentDossier?.claim,
        car: this.currentCar,
        glass: undefined,
        sinisterDate: sinisterDate ? new Date(sinisterDate) : undefined,
        sinisterPlace: undefined,
      },
      insurer: {
        ...this.currentDossier?.insurer,
        code: this.configurationService.getConfig()?.id,
      },
      creationData: {
        code: this.configurationService.getConfig()?.id,
        email: this.userLogin,
        origin: 'B2B',
      },
      policyInformation: {
        ...this.currentDossier?.policyInformation,
        insuranceChoice: insuranceChoice,
        insuranceCode: insurance?.insuranceCode,
        insuranceName: insurance?.insuranceName,
        policyNumber: this.form.get('contractNumber')?.value,
      },
      gpdr: this.form.get('gpdr')?.value,
      marketingData: this.form.get('marketingData')?.value,
    };
  }

  getUniqueInsuranceSuggestion():
    | {
        insuranceCode: string;
        insuranceName: string;
      }
    | undefined {
    let insuranceSuggestion:
      | {
          insuranceCode: string;
          insuranceName: string;
        }
      | undefined = undefined;
    if (
      this.form.get('insurerCompanyCode')?.value ||
      this.form.get('insurerCompany')?.value
    ) {
      insuranceSuggestion = {
        insuranceCode: this.form.get('insurerCompanyCode')?.value,
        insuranceName: this.form.get('insurerCompany')?.value,
      };
    } else if (
      this.currentDossier?.policyInformation?.insuranceCode ||
      this.currentDossier?.policyInformation?.insuranceName
    ) {
      insuranceSuggestion = {
        insuranceCode: this.currentDossier?.policyInformation?.insuranceCode,
        insuranceName: this.currentDossier?.policyInformation?.insuranceName,
      };
    } else if (
      this.configurationService.getConfig()?.insuranceSuggestion?.length === 1
    ) {
      const uniquInsurance =
        this.configurationService.getConfig()?.insuranceSuggestion[0];
      insuranceSuggestion = {
        insuranceCode: uniquInsurance.internalInsuranceCode
          ? uniquInsurance.internalInsuranceCode.toString()
          : uniquInsurance.insuranceCountryCode +
            uniquInsurance.insuranceCode.toString(),
        insuranceName: uniquInsurance?.insuranceName,
      };
    }

    return insuranceSuggestion;
  }

  private createDossier(
    dossier: Partial<Dossier>
  ): Observable<Partial<Dossier>> {
    this.dossierSB.setDossier(dossier);
    return this.dossierSB.createDossierAccount();
  }

  onValidLicensePlate(value: string): void {
    const licensePlateNumber = value;
    this.hasSearchCar = false;
    if (
      this.form.get('licensePlateNumber').invalid ||
      this.form.get('licensePlateNumber').hasError('license_plate_error')
    ) {
      this.currentCar = null;
    } else {
      if (
        !isSameLicensePlate(
          this.currentCar?.licensePlateNumber,
          this.form.get('licensePlateNumber')?.value
        )
      ) {
        this.displayLoading.next(true);
        this.dossierSB
          .fetchCarData(licensePlateNumber, null)
          .subscribe({
            next: (response) => {
              this.currentCar = generateNewCar(
                response.payload.car,
                this.configurationService.getAllLicensePlateCountries(),
                this.configurationService.getDefaultLicensePlateCountry()
              );
              this.currentDossier = {
                ...this.currentDossier,
                claim: {
                  ...this.currentDossier?.claim,
                  calibration: response?.payload?.calibration,
                },
              };
            },
            error: () => {
              this.currentCar = generateNewCar(
                { licensePlateNumber: licensePlateNumber },
                this.configurationService.getAllLicensePlateCountries(),
                this.configurationService.getDefaultLicensePlateCountry()
              );
            },
          })
          .add(() => {
            this.displayLoading.next(false);
            this.hasSearchCar = true;
            this.changeDetectorRef.detectChanges();
            document.getElementById('license-plate-infos').focus();
          });
      } else {
        this.hasSearchCar = true;
      }
    }
  }

  onCreateOrUpdateDossier(): Observable<Partial<Dossier>> {
    if (this.form.valid) {
      this.loadingService.setLoading(true);
      const formValues: Partial<Dossier> = this.formGroupToDossier();

      return this.dossierSB.dossier$.pipe(
        take(1),
        switchMap((dossier: Dossier) => {
          if (dossier?.id) {
            const updatedDossier = {
              ...formValues,
              id: dossier.id,
              claim: {
                ...formValues.claim,
                glass: dossier.claim.glass,
                sinisterPlace: dossier.claim.sinisterPlace,
                documents: dossier.claim.documents,
              },
              appointment: null,
            };

            return this.dossierSB.setDossier(updatedDossier).pipe(
              switchMap(() => {
                return this.dossierSB.updateDossier();
              })
            );
          } else {
            return this.createDossier(formValues).pipe(
              tap((createdDossier: Dossier) => {
                this.insightService.trackEvent({
                  name: `FrontApp [Insurer-Action] start new Dossier`,
                  properties: {
                    createdDossier,
                  },
                });
                this.currentDossier = createdDossier;
                this.authService.setCurrentDossierId(createdDossier.id);
              }),
              delayWhen((createdDossier) => {
                return this.dossierSB.setDossier({
                  ...createdDossier,
                  id: createdDossier.id,
                });
              })
            );
          }
        })
      );
    }
    return EMPTY;
  }

  onSendLink(): void {
    this.onCreateOrUpdateDossier()
      .pipe(
        take(1),
        switchMap((dossier: Dossier) => {
          return this.dossierSB.sendCustomerAccessEmail(dossier.id);
        })
      )
      .subscribe({
        next: (dossier: Dossier) => {
          this.loadingService.setLoading(false),
            (this.currentDossier = dossier);
          this.authService.setCurrentDossierId(dossier.id);
          this.snackbarService.showSuccess(
            this.translator.getInstantTranslation(
              'CustomerDataForm.smsMailSentSuccess'
            )
          );
        },
        error: (error) => {
          console.error(error);
          this.loadingService.setLoading(false),
            this.snackbarService.showError(
              this.translator.getInstantTranslation('CustomerDataForm.error'),
              this.translator.getInstantTranslation(
                'CustomerDataForm.errorMessage'
              )
            );
        },
      });
  }

  onValidate() {
    this.onCreateOrUpdateDossier().subscribe({
      next: () => {
        this.loadingService.setLoading(false),
          this.router
            .navigate(['/declaration/sinister-data'])
            .catch((error) => console.error(error));
      },
      error: (error) => {
        this.loadingService.setLoading(false), console.error(error);
      },
    });
  }

  onStepperTitleClick(clickedIndex: number) {
    if (clickedIndex === 1) {
      this.onValidate();
    }
  }

  canSearchLicensePlate(): boolean {
    return !!this.configurationService
      .getConfig()
      .patterns.licensePlate.find((lp) => lp.isSearchActive.insurer);
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
    this.dossierSubscriber?.unsubscribe();
    this.userLoginSubscriber.unsubscribe();
    this.productTourService.setDisplayHelp(false);
  }

  onInsurerCompanyChange($event) {
    const code =
      $event?.value === this.configurationService?.getConfig()?.id
        ? this.configurationService?.getConfig()?.id
        : $event?.value;

    const name =
      $event?.value === this.configurationService?.getConfig()?.id
        ? this.configurationService?.getConfig()?.insurerName
        : undefined;

    this.form.patchValue({ insurerCompany: name, insurerCompanyCode: code });
    this.changeDetectorRef.detectChanges();
  }

  onOtherInsurerCompanyChange($event) {
    this.form.patchValue({
      insurerCompany: $event.target.value,
    });
    this.changeDetectorRef.detectChanges();
  }

  getCarDisplayInfos() {
    if (this.currentCar?.model || this.currentCar?.manufacturer) {
      const carInfos = getCurrentCarDescription(
        this.currentCar,
        this.featureFlippingManager.isFeatureActivated(
          'display-vehicle-information-year'
        ),
        this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.licensePlateNumberDoorsLabel'
        ),
        dayjs
      );
      return carInfos;
    }
    return this.translator.getInstantTranslation(
      'CustomerDataForm.vehicleNotFound'
    );
  }

  getLicensePlateTooltipValue() {
    return this.translator.getInstantTranslation(
      'GlobalApplicativeConstants.licensePlateInfoTooltip.' +
        this.configurationService.getDefaultLicensePlateCountry()
    );
  }

  displayHint() {
    return this.clientAppId === 'customer'
      ? 'GlobalApplicativeConstants.accessibility.labels.emailExemple'
      : '';
  }

  hasValidForm(): boolean {
    return this.form?.valid && this.displayLoading.getValue() === false;
  }
}
